import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Chip,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  createChart,
  IChartApi,
  ISeriesApi,
  UTCTimestamp,
  CandlestickData,
  SeriesMarker,
  Time,
} from 'lightweight-charts';
import axios from '../utils/axios';
import TickerLink from './TickerLink';
import { pnlColors } from '../design/theme';

interface Candle {
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
}

interface CandlesResponse {
  ticker: string;
  candles: Candle[];
  trade_time: string;
  trade_candle_time: string;
  trade_price: number | null;
  action: string | null;
  matching_buy: { time: string; price: number } | null;
}

interface CandleChartModalProps {
  open: boolean;
  onClose: () => void;
  ticker: string;
  tradeTime: string | null;
  /** 'BUY' marks a buy point, 'SELL' marks a sell (and overlays the matching buy). */
  action: 'BUY' | 'SELL';
  /** The price the stock was bought/sold at, drawn as a dashed price line. */
  tradePrice?: number | null;
  traderId?: number;
  /** Optional header detail fields, shown only when provided.
   *  Positions pass shares + changePercent; transactions pass the P&L set. */
  shares?: number | null;
  changePercent?: number | null;
  profitAndLossPercent?: number | null;
  profitAndLoss?: number | null;
  volume?: number | null;
  marketCap?: number | null;
}

const formatUsd = (value: number): string => {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}$${Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPct = (value: number): string =>
  `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

const formatShares = (value: number): string =>
  value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 });

// Compact market-cap / volume formatting (e.g. 1.2B, 3.4M, 12.3K).
const formatCompact = (value: number): string =>
  value.toLocaleString(undefined, { notation: 'compact', maximumFractionDigits: 2 });

// One label/value pair in the header metadata row, in the app's uppercase-label idiom.
const Metric: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        display: 'block', fontSize: '0.68rem', fontWeight: 600, color: 'text.secondary',
        textTransform: 'uppercase', letterSpacing: '.05em',
      }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
      {children}
    </Typography>
  </Box>
);

// lightweight-charts renders time axis labels in UTC. Our timestamps arrive as
// Israel-local ISO strings (e.g. "...T16:55:00+03:00"); to make the chart show
// Israel wall-clock hours we map each instant to the UNIX epoch of the SAME
// wall-clock read as if it were UTC (i.e. shift by the Israel offset).
const toUnix = (iso: string): UTCTimestamp => {
  const d = new Date(iso);
  // getTimezoneOffset is the browser's offset; we instead want Israel wall-clock.
  // Reconstruct from the Israel-local parts encoded in the ISO offset by reading
  // the absolute instant and adding back the source offset embedded in `iso`.
  const offsetMatch = iso.match(/([+-])(\d{2}):(\d{2})$/);
  let offsetSeconds = 0;
  if (offsetMatch) {
    const sign = offsetMatch[1] === '-' ? -1 : 1;
    offsetSeconds = sign * (parseInt(offsetMatch[2], 10) * 3600 + parseInt(offsetMatch[3], 10) * 60);
  }
  // d.getTime() is the true UTC instant; adding the source offset yields a value
  // whose UTC rendering equals the original local wall-clock.
  return Math.floor(d.getTime() / 1000 + offsetSeconds) as UTCTimestamp;
};

// Format an instant in Israel time as "M/D/YYYY, HH:mm:ss" (24-hour).
const formatIsrael = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', {
    timeZone: 'Asia/Jerusalem',
    hour12: false,
  });

const CandleChartModal: React.FC<CandleChartModalProps> = ({
  open,
  onClose,
  ticker,
  tradeTime,
  action,
  tradePrice,
  traderId,
  shares,
  changePercent,
  profitAndLossPercent,
  profitAndLoss,
  volume,
  marketCap,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CandlesResponse | null>(null);

  // Fetch candles whenever the modal opens for a (ticker, tradeTime) pair.
  useEffect(() => {
    if (!open || !tradeTime) return;

    let cancelled = false;
    const fetchCandles = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({
          ticker,
          trade_time: tradeTime,
          action,
        });
        if (traderId != null) params.append('trader_id', String(traderId));
        if (tradePrice != null) params.append('trade_price', String(tradePrice));
        const response = await axios.get(`/candles?${params.toString()}`);
        if (!cancelled) setData(response.data);
      } catch (err) {
        console.error('Error fetching candles:', err);
        if (!cancelled) setError('Failed to load chart data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCandles();
    return () => {
      cancelled = true;
    };
  }, [open, ticker, tradeTime, action, tradePrice, traderId]);

  // (Re)build the chart once data is available and the container is mounted.
  useEffect(() => {
    if (!open || !data || !containerRef.current) return;

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: 360,
      layout: {
        background: { color: '#ffffff' },
        textColor: '#333333',
      },
      grid: {
        vertLines: { color: '#f0f0f0' },
        horzLines: { color: '#f0f0f0' },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        // Always render HH:mm so day-boundary date labels (e.g. "18", "22") don't
        // appear and confuse the hourly axis.
        tickMarkFormatter: (time: Time) => {
          const d = new Date((time as number) * 1000);
          const hh = String(d.getUTCHours()).padStart(2, '0');
          const mm = String(d.getUTCMinutes()).padStart(2, '0');
          return `${hh}:${mm}`;
        },
      },
    });
    chartRef.current = chart;

    const series = chart.addCandlestickSeries({
      upColor: '#4CAF50',
      downColor: '#F44336',
      borderUpColor: '#4CAF50',
      borderDownColor: '#F44336',
      wickUpColor: '#4CAF50',
      wickDownColor: '#F44336',
    });
    seriesRef.current = series;

    const candleData: CandlestickData[] = data.candles.map((c) => ({
      time: toUnix(c.time),
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    }));
    series.setData(candleData);

    const BUY_COLOR = '#2196F3';   // blue
    const SELL_COLOR = '#FF9800';  // orange

    // Markers anchor to the exact trade candle so they sit on the right bar.
    const anchor = toUnix(data.trade_candle_time);
    const firstTime = candleData.length ? (candleData[0].time as number) : 0;
    const lastTime = candleData.length ? (candleData[candleData.length - 1].time as number) : 0;
    const markers: SeriesMarker<Time>[] = [];
    if (data.candles.length > 0) {
      if (action === 'SELL') {
        // SELL: orange arrow above the candle, pointing down.
        markers.push({
          time: anchor,
          position: 'aboveBar',
          color: SELL_COLOR,
          shape: 'arrowDown',
          text: 'SELL',
        });
        // Overlay the matching buy if it falls within the visible window. Snap it
        // to the candle that contains it (last candle whose open <= buy time) so
        // the arrow lands on a real bar.
        if (data.matching_buy) {
          const buyTime = toUnix(data.matching_buy.time) as number;
          if (buyTime >= firstTime && buyTime <= lastTime) {
            let buyAnchor = firstTime;
            for (const c of candleData) {
              if ((c.time as number) <= buyTime) buyAnchor = c.time as number;
              else break;
            }
            markers.push({
              time: buyAnchor as UTCTimestamp,
              position: 'belowBar',
              color: BUY_COLOR,
              shape: 'arrowUp',
              text: 'BUY',
            });
          }
        }
      } else {
        // BUY: blue arrow below the candle, pointing up.
        markers.push({
          time: anchor,
          position: 'belowBar',
          color: BUY_COLOR,
          shape: 'arrowUp',
          text: 'BUY',
        });
      }
      // Markers must be sorted by time ascending.
      markers.sort((a, b) => (a.time as number) - (b.time as number));
      series.setMarkers(markers);
    }

    // Horizontal dashed price line at the trade price: blue for buy, orange for sell.
    if (data.trade_price != null) {
      series.createPriceLine({
        price: data.trade_price,
        color: action === 'SELL' ? SELL_COLOR : BUY_COLOR,
        lineWidth: 2,
        lineStyle: 2, // dashed
        axisLabelVisible: true,
        title: action === 'SELL' ? 'Sell' : 'Buy',
      });
    }

    // On a SELL chart, also draw the matching buy's price as a blue dashed line so
    // the original entry is visible even when the buy happened before this window.
    if (action === 'SELL' && data.matching_buy) {
      series.createPriceLine({
        price: data.matching_buy.price,
        color: BUY_COLOR,
        lineWidth: 2,
        lineStyle: 2, // dashed
        axisLabelVisible: true,
        title: 'Buy',
      });
    }

    chart.timeScale().fitContent();

    const handleResize = () => {
      if (containerRef.current) {
        chart.applyOptions({ width: containerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [open, data, action]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TickerLink ticker={ticker} variant="h6" fontWeight={800} />
            <Chip
              label={action === 'SELL' ? 'Sell' : 'Buy'}
              size="small"
              sx={{
                height: 20, fontWeight: 700,
                ...(action === 'SELL'
                  ? { bgcolor: 'rgba(198, 40, 40, 0.1)', color: pnlColors.loss }
                  : { bgcolor: 'primary.light', color: 'primary.dark' }),
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1 }}>
            {tradeTime && (
              <Metric label={`${action === 'SELL' ? 'Sell' : 'Buy'} time`}>
                {formatIsrael(tradeTime)}
              </Metric>
            )}
            {shares != null && <Metric label="Shares">{formatShares(shares)}</Metric>}
            {changePercent != null && (
              <Metric label="Change">
                <Box component="span" sx={{ color: changePercent >= 0 ? pnlColors.gain : pnlColors.loss }}>
                  {formatPct(changePercent)}
                </Box>
              </Metric>
            )}
            {profitAndLossPercent != null && (
              <Metric label="P&L %">
                <Box component="span" sx={{ color: profitAndLossPercent >= 0 ? pnlColors.gain : pnlColors.loss }}>
                  {formatPct(profitAndLossPercent)}
                </Box>
              </Metric>
            )}
            {profitAndLoss != null && (
              <Metric label="P&L">
                <Box component="span" sx={{ color: profitAndLoss >= 0 ? pnlColors.gain : pnlColors.loss }}>
                  {formatUsd(profitAndLoss)}
                </Box>
              </Metric>
            )}
            {volume != null && <Metric label="Volume">{formatCompact(volume)}</Metric>}
            {marketCap != null && <Metric label="Market cap">{formatCompact(marketCap)}</Metric>}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={360}>
            <CircularProgress />
          </Box>
        )}
        {!loading && error && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={360}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        {!loading && !error && data && data.candles.length === 0 && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={360}>
            <Typography color="text.secondary">
              No hourly candle data available for this trade.
            </Typography>
          </Box>
        )}
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            display: !loading && !error && data && data.candles.length > 0 ? 'block' : 'none',
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default CandleChartModal;
